import { useState } from 'react'
import { Settings, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import type { SchemaField } from '../../../types/schede'
import { DB_LOOKUP_ENTITIES } from '../../../types/schede'

interface PropertyPanelProps {
  field: SchemaField | null
  allFields: SchemaField[]
  onUpdate: (updates: Partial<SchemaField>) => void
}

export default function PropertyPanel({ field, allFields, onUpdate }: PropertyPanelProps) {
  const [showVisibility, setShowVisibility] = useState(false)
  const [showValidation, setShowValidation] = useState(false)
  const [showOptions, setShowOptions] = useState(true)

  if (!field) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6" style={{ color: 'var(--color-text-tertiary)' }}>
        <Settings className="w-8 h-8 mb-2" />
        <p className="text-sm text-center">Seleziona un campo per modificarne le proprieta'</p>
      </div>
    )
  }

  const inputStyle = {
    backgroundColor: 'var(--color-bg-input, var(--color-bg-card))',
    borderColor: 'var(--color-border)',
    color: 'var(--color-text-primary)',
  }

  // Fields that can be referenced (for visibility rules)
  const referencableFields = allFields.filter(f => f.id !== field.id && f.type !== 'section')

  // Determina se il campo e' dentro una sezione (per mostrare toggle "fuori sezione")
  const isInsideSection = (() => {
    if (field.type === 'section') return false
    const sorted = [...allFields].sort((a, b) => a.ordine - b.ordine)
    const idx = sorted.findIndex(f => f.id === field.id)
    // Risali all'indietro: cerca una sezione, fermati se trovi un outside_section
    for (let i = idx - 1; i >= 0; i--) {
      if (sorted[i].type === 'section') return true
      if (sorted[i].outside_section) return false
    }
    return false
  })()

  return (
    <div className="p-4 space-y-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-tertiary)' }}>
        Proprieta'
      </h3>

      {/* Label */}
      <div>
        <label className="text-xs font-medium block mb-1">Label</label>
        <input
          type="text"
          value={field.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
          className="w-full px-2.5 py-1.5 rounded text-sm border"
          style={inputStyle}
        />
      </div>

      {/* Name (machine name) */}
      <div>
        <label className="text-xs font-medium block mb-1">Nome campo</label>
        <input
          type="text"
          value={field.name}
          onChange={(e) => onUpdate({ name: e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase() })}
          className="w-full px-2.5 py-1.5 rounded text-sm border font-mono"
          style={inputStyle}
        />
      </div>

      {/* Label text (for label type) */}
      {field.type === 'label' && (
        <div>
          <label className="text-xs font-medium block mb-1">Testo etichetta</label>
          <textarea
            value={field.label_text || ''}
            onChange={(e) => onUpdate({ label_text: e.target.value })}
            className="w-full px-2.5 py-1.5 rounded text-sm border"
            style={inputStyle}
            rows={3}
            placeholder="Testo descrittivo o domanda lunga..."
          />
          <p className="text-[10px] mt-1" style={{ color: 'var(--color-text-tertiary)' }}>
            Se vuoto, viene mostrata la label principale
          </p>
        </div>
      )}

      {/* Required + Width row */}
      {!['section', 'label'].includes(field.type) && (
        <div className="flex gap-3">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => onUpdate({ required: e.target.checked })}
              className="rounded"
            />
            Obbligatorio
          </label>
          <select
            value={field.width || 'full'}
            onChange={(e) => onUpdate({ width: e.target.value as any })}
            className="px-2 py-1 rounded text-xs border flex-1"
            style={inputStyle}
          >
            <option value="full">Intera riga</option>
            <option value="half">Meta' riga</option>
            <option value="third">Un terzo</option>
          </select>
        </div>
      )}

      {/* Outside section toggle */}
      {field.type !== 'section' && (isInsideSection || field.outside_section) && (
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={!!field.outside_section}
            onChange={(e) => onUpdate({ outside_section: e.target.checked || undefined })}
            className="rounded"
          />
          <span style={{ color: field.outside_section ? 'var(--color-warning, #f59e0b)' : undefined }}>
            Fuori sezione
          </span>
        </label>
      )}

      {/* Placeholder (text types) */}
      {['text', 'textarea', 'number', 'date'].includes(field.type) && (
        <div>
          <label className="text-xs font-medium block mb-1">Placeholder</label>
          <input
            type="text"
            value={field.placeholder || ''}
            onChange={(e) => onUpdate({ placeholder: e.target.value || undefined })}
            className="w-full px-2.5 py-1.5 rounded text-sm border"
            style={inputStyle}
          />
        </div>
      )}

      {/* Crea Scadenza (solo per campi date) */}
      {field.type === 'date' && (
        <div className="space-y-2 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-main, #f5f5f5)' }}>
          <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
            <input
              type="checkbox"
              checked={field.crea_scadenza || false}
              onChange={(e) => onUpdate({ crea_scadenza: e.target.checked || undefined, crea_scadenza_assegnazione: e.target.checked ? 'owner' : undefined })}
              className="rounded"
            />
            Crea scadenza automatica
          </label>
          {field.crea_scadenza && (
            <div>
              <label className="text-xs block mb-1" style={{ color: 'var(--color-text-tertiary)' }}>Assegna a</label>
              <div className="flex gap-2">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="radio"
                    name={`scadenza_assegna_${field.id}`}
                    checked={field.crea_scadenza_assegnazione === 'owner'}
                    onChange={() => onUpdate({ crea_scadenza_assegnazione: 'owner' })}
                  />
                  Proprietario
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="radio"
                    name={`scadenza_assegna_${field.id}`}
                    checked={field.crea_scadenza_assegnazione === 'seleziona'}
                    onChange={() => onUpdate({ crea_scadenza_assegnazione: 'seleziona' })}
                  />
                  Seleziona utente
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help text */}
      <div>
        <label className="text-xs font-medium block mb-1">Testo aiuto</label>
        <input
          type="text"
          value={field.help_text || ''}
          onChange={(e) => onUpdate({ help_text: e.target.value || undefined })}
          className="w-full px-2.5 py-1.5 rounded text-sm border"
          style={inputStyle}
          placeholder="Tooltip opzionale..."
        />
      </div>

      {/* === OPTIONS (select, radio) === */}
      {['select', 'radio'].includes(field.type) && (
        <div>
          <button
            onClick={() => setShowOptions(!showOptions)}
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide w-full"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {showOptions ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Opzioni
          </button>
          {showOptions && (
            <div className="mt-2 space-y-1.5">
              {(field.options || []).map((opt, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={opt.label}
                    onChange={(e) => {
                      const newOpts = [...(field.options || [])]
                      newOpts[i] = { ...newOpts[i], label: e.target.value }
                      onUpdate({ options: newOpts })
                    }}
                    className="flex-1 px-2 py-1 rounded text-xs border"
                    style={inputStyle}
                    placeholder="Label"
                  />
                  <input
                    type="text"
                    value={opt.value}
                    onChange={(e) => {
                      const newOpts = [...(field.options || [])]
                      newOpts[i] = { ...newOpts[i], value: e.target.value }
                      onUpdate({ options: newOpts })
                    }}
                    className="w-20 px-2 py-1 rounded text-xs border font-mono"
                    style={inputStyle}
                    placeholder="Valore"
                  />
                  <button
                    onClick={() => {
                      const newOpts = (field.options || []).filter((_, j) => j !== i)
                      onUpdate({ options: newOpts })
                    }}
                    className="p-0.5"
                    style={{ color: 'var(--color-danger, #ef4444)' }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const newOpts = [...(field.options || []), { label: '', value: '' }]
                  onUpdate({ options: newOpts })
                }}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded"
                style={{ color: 'var(--color-primary)' }}
              >
                <Plus className="w-3 h-3" /> Aggiungi opzione
              </button>
            </div>
          )}
        </div>
      )}

      {/* === SECTION CONFIG === */}
      {field.type === 'section' && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={field.section_collapsible ?? true}
              onChange={(e) => onUpdate({ section_collapsible: e.target.checked })}
              className="rounded"
            />
            Comprimibile
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={field.section_default_open ?? true}
              onChange={(e) => onUpdate({ section_default_open: e.target.checked })}
              className="rounded"
            />
            Aperta di default
          </label>
        </div>
      )}

      {/* === TABLE COLUMNS === */}
      {field.type === 'table' && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-text-tertiary)' }}>
            Colonne tabella
          </p>
          <div className="space-y-1.5">
            {(field.table_columns || []).map((col, i) => (
              <div key={col.id} className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={col.label}
                  onChange={(e) => {
                    const cols = [...(field.table_columns || [])]
                    cols[i] = { ...cols[i], label: e.target.value }
                    onUpdate({ table_columns: cols })
                  }}
                  className="flex-1 px-2 py-1 rounded text-xs border"
                  style={inputStyle}
                  placeholder="Intestazione"
                />
                <select
                  value={col.type}
                  onChange={(e) => {
                    const cols = [...(field.table_columns || [])]
                    cols[i] = { ...cols[i], type: e.target.value as any }
                    onUpdate({ table_columns: cols })
                  }}
                  className="w-20 px-1 py-1 rounded text-xs border"
                  style={inputStyle}
                >
                  <option value="text">Testo</option>
                  <option value="number">Numero</option>
                  <option value="date">Data</option>
                  <option value="select">Select</option>
                  <option value="checkbox">Check</option>
                </select>
                <button
                  onClick={() => {
                    const cols = (field.table_columns || []).filter((_, j) => j !== i)
                    onUpdate({ table_columns: cols })
                  }}
                  className="p-0.5"
                  style={{ color: 'var(--color-danger, #ef4444)' }}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                const cols = [...(field.table_columns || []), { id: crypto.randomUUID(), label: '', type: 'text' as const }]
                onUpdate({ table_columns: cols })
              }}
              className="flex items-center gap-1 text-xs px-2 py-1 rounded"
              style={{ color: 'var(--color-primary)' }}
            >
              <Plus className="w-3 h-3" /> Aggiungi colonna
            </button>
          </div>

          <div className="flex gap-3 mt-3">
            <div>
              <label className="text-[10px] block mb-0.5">Min righe</label>
              <input
                type="number"
                value={field.table_min_rows ?? 1}
                onChange={(e) => onUpdate({ table_min_rows: parseInt(e.target.value) || 0 })}
                className="w-16 px-2 py-1 rounded text-xs border"
                style={inputStyle}
                min={0}
              />
            </div>
            <div>
              <label className="text-[10px] block mb-0.5">Max righe</label>
              <input
                type="number"
                value={field.table_max_rows ?? 50}
                onChange={(e) => onUpdate({ table_max_rows: parseInt(e.target.value) || 50 })}
                className="w-16 px-2 py-1 rounded text-xs border"
                style={inputStyle}
                min={1}
              />
            </div>
          </div>
        </div>
      )}

      {/* === CALCULATED FORMULA === */}
      {field.type === 'calculated' && (
        <div>
          <label className="text-xs font-medium block mb-1">Formula</label>
          <input
            type="text"
            value={field.calculation_formula || ''}
            onChange={(e) => onUpdate({ calculation_formula: e.target.value })}
            className="w-full px-2.5 py-1.5 rounded text-sm border font-mono"
            style={inputStyle}
            placeholder="{{campo_a}} + {{campo_b}}"
          />
          <p className="text-[10px] mt-1" style={{ color: 'var(--color-text-tertiary)' }}>
            Usa {'{{nome_campo}}'} per i riferimenti. Aggregati: SUM, AVG, COUNT, MIN, MAX per colonne tabella.
          </p>
          <div className="mt-2">
            <label className="text-[10px] block mb-0.5">Decimali</label>
            <input
              type="number"
              value={field.calculation_precision ?? 2}
              onChange={(e) => onUpdate({ calculation_precision: parseInt(e.target.value) || 2 })}
              className="w-16 px-2 py-1 rounded text-xs border"
              style={inputStyle}
              min={0}
              max={10}
            />
          </div>
        </div>
      )}

      {/* === READONLY VALUE === */}
      {field.type === 'readonly' && (
        <div>
          <label className="text-xs font-medium block mb-1">Valore</label>
          <input
            type="text"
            value={field.readonly_value || ''}
            onChange={(e) => onUpdate({ readonly_value: e.target.value })}
            className="w-full px-2.5 py-1.5 rounded text-sm border"
            style={inputStyle}
            placeholder="Testo statico o {{variabile}}"
          />
        </div>
      )}

      {/* === DB LOOKUP CONFIG === */}
      {field.type === 'db_lookup' && (
        <div>
          <label className="text-xs font-medium block mb-1">Tipo entita'</label>
          <select
            value={field.db_lookup_entity || 'clienti'}
            onChange={(e) => onUpdate({ db_lookup_entity: e.target.value })}
            className="w-full px-2.5 py-1.5 rounded text-sm border"
            style={inputStyle}
          >
            {Object.entries(DB_LOOKUP_ENTITIES).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <p className="text-[10px] mt-1" style={{ color: 'var(--color-text-tertiary)' }}>
            Seleziona quale anagrafica cercare
          </p>
        </div>
      )}

      {/* === FILE/PHOTO CONFIG === */}
      {['file', 'photo'].includes(field.type) && (
        <div>
          <label className="text-xs font-medium block mb-1">Max dimensione (MB)</label>
          <input
            type="number"
            value={field.file_max_size_mb ?? 10}
            onChange={(e) => onUpdate({ file_max_size_mb: parseInt(e.target.value) || 10 })}
            className="w-24 px-2.5 py-1.5 rounded text-sm border"
            style={inputStyle}
            min={1}
          />
        </div>
      )}

      {/* === VALIDATION (number) === */}
      {field.type === 'number' && (
        <div>
          <button
            onClick={() => setShowValidation(!showValidation)}
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide w-full"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {showValidation ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Validazione
          </button>
          {showValidation && (
            <div className="mt-2 flex gap-3">
              <div>
                <label className="text-[10px] block mb-0.5">Min</label>
                <input
                  type="number"
                  value={field.validation?.min ?? ''}
                  onChange={(e) => onUpdate({
                    validation: { ...field.validation, min: e.target.value ? parseFloat(e.target.value) : undefined }
                  })}
                  className="w-20 px-2 py-1 rounded text-xs border"
                  style={inputStyle}
                />
              </div>
              <div>
                <label className="text-[10px] block mb-0.5">Max</label>
                <input
                  type="number"
                  value={field.validation?.max ?? ''}
                  onChange={(e) => onUpdate({
                    validation: { ...field.validation, max: e.target.value ? parseFloat(e.target.value) : undefined }
                  })}
                  className="w-20 px-2 py-1 rounded text-xs border"
                  style={inputStyle}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* === TEXT VALIDATION === */}
      {['text', 'textarea'].includes(field.type) && (
        <div>
          <button
            onClick={() => setShowValidation(!showValidation)}
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide w-full"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {showValidation ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Validazione
          </button>
          {showValidation && (
            <div className="mt-2 space-y-2">
              <div className="flex gap-3">
                <div>
                  <label className="text-[10px] block mb-0.5">Min lunghezza</label>
                  <input
                    type="number"
                    value={field.validation?.minLength ?? ''}
                    onChange={(e) => onUpdate({
                      validation: { ...field.validation, minLength: e.target.value ? parseInt(e.target.value) : undefined }
                    })}
                    className="w-20 px-2 py-1 rounded text-xs border"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className="text-[10px] block mb-0.5">Max lunghezza</label>
                  <input
                    type="number"
                    value={field.validation?.maxLength ?? ''}
                    onChange={(e) => onUpdate({
                      validation: { ...field.validation, maxLength: e.target.value ? parseInt(e.target.value) : undefined }
                    })}
                    className="w-20 px-2 py-1 rounded text-xs border"
                    style={inputStyle}
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] block mb-0.5">Pattern regex</label>
                <input
                  type="text"
                  value={field.validation?.pattern ?? ''}
                  onChange={(e) => onUpdate({
                    validation: { ...field.validation, pattern: e.target.value || undefined }
                  })}
                  className="w-full px-2 py-1 rounded text-xs border font-mono"
                  style={inputStyle}
                  placeholder="^[A-Z]{2}[0-9]{3}$"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* === VISIBILITY RULES === */}
      {field.type !== 'section' && (
        <div>
          <button
            onClick={() => setShowVisibility(!showVisibility)}
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide w-full"
            style={{ color: 'var(--color-text-tertiary)' }}
          >
            {showVisibility ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Regole visibilita' ({(field.visibility_rules || []).length})
          </button>
          {showVisibility && (
            <div className="mt-2 space-y-2">
              {(field.visibility_rules || []).map((rule, i) => (
                <div key={i} className="p-2 rounded border text-xs space-y-1.5" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-card)' }}>
                  {i > 0 && (
                    <select
                      value={rule.logic || 'and'}
                      onChange={(e) => {
                        const rules = [...(field.visibility_rules || [])]
                        rules[i] = { ...rules[i], logic: e.target.value as any }
                        onUpdate({ visibility_rules: rules })
                      }}
                      className="px-1 py-0.5 rounded border text-[10px]"
                      style={inputStyle}
                    >
                      <option value="and">E (AND)</option>
                      <option value="or">O (OR)</option>
                    </select>
                  )}
                  <div className="flex items-center gap-1">
                    <select
                      value={rule.field_id}
                      onChange={(e) => {
                        const rules = [...(field.visibility_rules || [])]
                        rules[i] = { ...rules[i], field_id: e.target.value }
                        onUpdate({ visibility_rules: rules })
                      }}
                      className="flex-1 px-1 py-1 rounded border"
                      style={inputStyle}
                    >
                      <option value="">-- Campo --</option>
                      {referencableFields.map(f => (
                        <option key={f.id} value={f.id}>{f.name}{f.label !== f.name ? ` (${f.label})` : ''}</option>
                      ))}
                    </select>
                    <select
                      value={rule.operator}
                      onChange={(e) => {
                        const rules = [...(field.visibility_rules || [])]
                        rules[i] = { ...rules[i], operator: e.target.value as any }
                        onUpdate({ visibility_rules: rules })
                      }}
                      className="w-16 px-1 py-1 rounded border"
                      style={inputStyle}
                    >
                      <option value="eq">=</option>
                      <option value="neq">!=</option>
                      <option value="gt">&gt;</option>
                      <option value="lt">&lt;</option>
                      <option value="contains">contiene</option>
                      <option value="empty">vuoto</option>
                      <option value="not_empty">pieno</option>
                    </select>
                    {!['empty', 'not_empty'].includes(rule.operator) && (
                      <input
                        type="text"
                        value={rule.value ?? ''}
                        onChange={(e) => {
                          const rules = [...(field.visibility_rules || [])]
                          rules[i] = { ...rules[i], value: e.target.value }
                          onUpdate({ visibility_rules: rules })
                        }}
                        className="w-20 px-1 py-1 rounded border"
                        style={inputStyle}
                        placeholder="Valore"
                      />
                    )}
                    <button
                      onClick={() => {
                        const rules = (field.visibility_rules || []).filter((_, j) => j !== i)
                        onUpdate({ visibility_rules: rules })
                      }}
                      style={{ color: 'var(--color-danger, #ef4444)' }}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={() => {
                  const rules = [...(field.visibility_rules || []), {
                    field_id: '',
                    operator: 'eq' as const,
                    value: '',
                    logic: 'and' as const,
                  }]
                  onUpdate({ visibility_rules: rules })
                }}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded"
                style={{ color: 'var(--color-primary)' }}
              >
                <Plus className="w-3 h-3" /> Aggiungi regola
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
